import { LitElement, html, css, nothing } from "lit";

export class RDBreadcrumbs extends LitElement {
  static properties = {
    path: { type: Array },
    _canScrollLeft: { type: Boolean, state: true },
    _canScrollRight: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: var(--bar-height);
      background-color: var(--ide-bg);
      border-top: 1px solid var(--ide-border);
      user-select: none;
      box-sizing: border-box;
      font-family: var(--font-code);
      font-size: 12px;
      position: relative;
    }

    .wrapper {
      display: flex;
      align-items: center;
      height: 100%;
      position: relative;
      overflow: hidden; /* Ensure buttons don't spill out */
    }

    .scroll-viewport {
      flex: 1;
      display: flex;
      align-items: center;
      height: 100%;
      /* Add padding to ensure content isn't hidden behind buttons at start/end */
      padding: 0 24px;
      gap: 2px;
      overflow-x: auto;
      scrollbar-width: none;
      scroll-behavior: smooth;
    }

    .scroll-viewport::-webkit-scrollbar {
      display: none;
    }

    /* --- Breadcrumb Item --- */
    .node {
      display: flex;
      align-items: center;
      padding: 0 4px;
      height: 20px;
      border-radius: 2px;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.1s;
      color: var(--ide-fg);
      border-bottom: 1px solid transparent;
    }
    .node:hover {
      transition: all 0.1s;
      filter: brightness(1.3);
      border-bottom-color: var(--syntax-tag);
    }

    /* --- Syntax Highlighting --- */
    .punct {
      color: var(--syntax-punct);
      margin: 0 1px;
    }
    .tag {
      color: var(--syntax-tag);
      font-weight: 500;
    }
    .id {
      color: var(--syntax-id);
    }
    .cls {
      color: var(--syntax-cls);
    }

    .meta {
      margin-left: 6px;
      display: flex;
      align-items: center;
      opacity: 0.9;
    }

    .cls-text {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      vertical-align: bottom;
    }

    /*.node:hover .cls-text {
      max-width: none;
    }*/

    .separator {
      color: var(--syntax-punct);
      opacity: 0.6;
      font-size: 10px;
      margin: 0 2px;
    }

    /* --- Sticky Carousel Buttons (Absolute) --- */
    .nav-btn {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 24px;
      display: flex;
      align-items: center;
      justify-content: center;

      background-color: var(--btn-bg); /* Solid background to cover content */
      color: var(--syntax-punct);

      cursor: pointer;
      z-index: 20;
      border: none;
      font-family: var(--font-code);
      font-weight: bold;
      font-size: 14px;

      /* Smooth appearance */
      transition:
        opacity 0.2s ease,
        background-color 0.1s;
      opacity: 1;
    }

    .nav-btn:hover {
      background-color: var(--btn-hover-bg);
      color: var(--ide-fg);
    }

    /* Positioning & Shadows */
    .nav-btn.left {
      left: 0;
      border-right: 1px solid var(--ide-border);
      box-shadow: 2px 0 5px var(--btn-shadow);
    }

    .nav-btn.right {
      right: 0;
      border-left: 1px solid var(--ide-border);
      box-shadow: -2px 0 5px var(--btn-shadow);
    }

    .nav-btn.hidden {
      opacity: 0;
      pointer-events: none;
    }
  `;

  constructor() {
    super();
    this.path = [];
    this._canScrollLeft = false;
    this._canScrollRight = false;
    this._resizeObserver = null;
  }

  /* -----------------------------------------------------------
         NEW: Element Parsing Logic
         Usage: el.element = document.querySelector('#my-div');
         ----------------------------------------------------------- */
  set element(domElement) {
    if (!domElement) {
      this.path = [];
    } else {
      this.path = this._parsePath(domElement);
    }
    // Force scroll update after new path render
    setTimeout(() => {
      this._checkScroll();
      this._scrollToEnd();
    }, 50);
  }

  _parsePath(el) {
    const path = [];
    let current = el;

    // Traverse up until we hit the body or run out of parents
    while (current && current.nodeType === 1) {
      // 1 = Element Node
      if (current.tagName === "HTML") break; // Stop before HTML

      // Build the node object
      path.unshift({
        type: current.tagName.toLowerCase(),
        elementId: current.id || "",
        classes: typeof current.className === "string" ? current.className.trim() : "",
        name: current.getAttribute("name") || "",
        // Store ref if we want to highlight back on hover
        _ref: current,
      });

      if (current.tagName === "BODY") break; // Stop at body
      current = current.parentElement;
    }
    return path;
  }

  _scrollToEnd() {
    const el = this.shadowRoot.querySelector(".scroll-viewport");
    if (el) {
      el.scrollLeft = el.scrollWidth;
    }
  }
  /* ----------------------------------------------------------- */

  firstUpdated() {
    const viewport = this.shadowRoot.querySelector(".scroll-viewport");

    // Mouse Wheel Support: Map vertical scroll to horizontal
    viewport.addEventListener(
      "wheel",
      (e) => {
        // Check if it's primarily a vertical scroll (standard mouse wheel)
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          // Scroll horizontally
          viewport.scrollLeft += e.deltaY;
        }
      },
      { passive: false },
    );

    // Use ResizeObserver for more robust size tracking
    this._resizeObserver = new ResizeObserver(() => this._checkScroll());
    this._resizeObserver.observe(viewport);

    viewport.addEventListener("scroll", () => this._checkScroll());
    document.addEventListener(canvasEventsLabels.canvasUpdated, this.onElementSelected.bind(this));
    this._checkScroll();
  }
  onElementSelected() {
    this.element = CanvasManager.selectedElement;
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    document.removeEventListener(canvasEventsLabels.canvasUpdated, this.onElementSelected.bind(this));
  }

  updated(changedProperties) {
    if (changedProperties.has("path")) {
      setTimeout(() => this._checkScroll(), 0);
    }
  }

  _checkScroll() {
    const el = this.shadowRoot.querySelector(".scroll-viewport");
    if (!el) return;

    const tolerance = 2; // Pixel buffer to prevent flickering
    const scrollLeft = el.scrollLeft;
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;

    this._canScrollLeft = scrollLeft > tolerance;
    this._canScrollRight = scrollWidth - clientWidth - scrollLeft > tolerance;
  }

  _scroll(direction) {
    const el = this.shadowRoot.querySelector(".scroll-viewport");
    const amount = 200;
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  }

  _handleClick(node, index) {
    // Dispatch the selected node AND the original DOM element if available
    this.dispatchEvent(
      new CustomEvent("node-select", {
        detail: {
          node,
          index,
          domElement: node._ref,
        },
      }),
    );
    CanvasManager.selectElement(node._ref, true, true);
  }
  _hoverNode(node, isHover) {
    if (!node || !node._ref) return;
    this.dispatchEvent(
      new CustomEvent("node-hover", {
        detail: {
          node,
          isHover,
        },
      }),
    );
    if (isHover) {
      CanvasManager.highlightElement(node._ref);
    } else {
      CanvasManager.unhighlightElement();
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <!-- Left Arrow -->
        <button class="nav-btn left ${this._canScrollLeft ? "" : "hidden"}" @click="${() => this._scroll(-1)}" title="Scroll Left">&lt;</button>

        <!-- Content Area -->
        <div class="scroll-viewport">
          ${this.path.map((node, index) => {
            return html`
              ${index > 0 ? html`<span class="separator">/</span>` : nothing}

              <div class="node" @click="${() => this._handleClick(node, index)}" @mouseenter="${() => this._hoverNode(node, true)}" @mouseleave="${() => this._hoverNode(node, false)}">
                <span class="punct">&lt;</span>
                <span class="tag">${node.type}</span>
                ${this._renderMeta(node)}
              </div>
            `;
          })}
        </div>

        <!-- Right Arrow -->
        <button class="nav-btn right ${this._canScrollRight ? "" : "hidden"}" @click="${() => this._scroll(1)}" title="Scroll Right">&gt;</button>
      </div>
    `;
  }

  _renderMeta(node) {
    if (!node.elementId && !node.classes) return nothing;

    return html`
      <div class="meta">${node.elementId ? html` <span class="punct">#</span><span class="id">${node.elementId}</span> ` : nothing} ${node.classes ? html` <span class="punct">.</span><span class="cls cls-text">${node.classes.split(" ").join(".")}</span> ` : nothing}</div>
    `;
  }
}

customElements.define("rd-breadcrumbs", RDBreadcrumbs);
